import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ArrowRight, 
  User, 
  Target, 
  Dumbbell, 
  Utensils, 
  Heart, 
  CheckCircle,
  Loader2,
  Crown
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: "basic", title: "Datos Básicos", icon: User },
  { id: "goal", title: "Objetivo", icon: Target },
  { id: "training", title: "Entrenamiento", icon: Dumbbell },
  { id: "nutrition", title: "Nutrición", icon: Utensils },
  { id: "lifestyle", title: "Estilo de Vida", icon: Heart },
];

const PremiumOnboarding = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic
    peso: "",
    altura: "",
    edad: "",
    genero: "",
    // Goal
    objetivo_principal: "",
    peso_objetivo: "",
    motivacion: "",
    // Training
    experiencia: "",
    dias_disponibles: "",
    lesiones: "",
    equipo_disponible: "",
    // Nutrition
    alergias: "",
    comidas_no_gustan: "",
    comidas_favoritas: "",
    horario_comidas: "",
    suplementos_actuales: "",
    // Lifestyle
    nivel_estres: 5,
    horas_sueno: "",
    nivel_actividad: "",
    tipo_trabajo: "",
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Basic
        return formData.peso && formData.altura && formData.edad && formData.genero;
      case 1: // Goal
        return formData.objetivo_principal;
      case 2: // Training
        return formData.experiencia && formData.dias_disponibles;
      case 3: // Nutrition
        return true; // Optional
      case 4: // Lifestyle
        return true; // Optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        peso: parseFloat(formData.peso),
        altura: parseFloat(formData.altura),
        edad: parseInt(formData.edad),
        peso_objetivo: formData.peso_objetivo ? parseFloat(formData.peso_objetivo) : null,
        dias_disponibles: parseInt(formData.dias_disponibles),
        horas_sueno: formData.horas_sueno ? parseFloat(formData.horas_sueno) : null,
        nivel_estres: formData.nivel_estres || null,
      };

      await axios.post(`${API_URL}/api/premium/onboarding`, payload, {
        headers: getAuthHeaders(),
      });

      toast.success("¡Perfil completado!");
      navigate("/premium/pending");
    } catch (error) {
      console.error("Error submitting onboarding:", error);
      toast.error("Error al guardar el perfil");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Peso actual (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="75"
                  value={formData.peso}
                  onChange={(e) => updateField("peso", e.target.value)}
                  className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Altura (cm)</Label>
                <Input
                  type="number"
                  placeholder="175"
                  value={formData.altura}
                  onChange={(e) => updateField("altura", e.target.value)}
                  className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Edad</Label>
              <Input
                type="number"
                placeholder="30"
                value={formData.edad}
                onChange={(e) => updateField("edad", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-3 block">Género</Label>
              <RadioGroup
                value={formData.genero}
                onValueChange={(value) => updateField("genero", value)}
                className="grid grid-cols-2 gap-3"
              >
                <Label
                  htmlFor="masculino"
                  className={cn(
                    "flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all",
                    formData.genero === "masculino"
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                  )}
                >
                  <RadioGroupItem value="masculino" id="masculino" className="sr-only" />
                  <span className="text-white font-medium">Masculino</span>
                </Label>
                <Label
                  htmlFor="femenino"
                  className={cn(
                    "flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all",
                    formData.genero === "femenino"
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                  )}
                >
                  <RadioGroupItem value="femenino" id="femenino" className="sr-only" />
                  <span className="text-white font-medium">Femenino</span>
                </Label>
              </RadioGroup>
            </div>
          </div>
        );

      case 1: // Goal
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-white mb-3 block">¿Cuál es tu objetivo principal?</Label>
              <RadioGroup
                value={formData.objetivo_principal}
                onValueChange={(value) => updateField("objetivo_principal", value)}
                className="space-y-3"
              >
                {[
                  { value: "perdida_grasa", label: "Perder grasa", desc: "Definir y reducir % graso" },
                  { value: "recomposicion", label: "Recomposición", desc: "Perder grasa y ganar músculo" },
                  { value: "ganancia_muscular", label: "Ganar músculo", desc: "Aumentar masa muscular" },
                ].map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={option.value}
                    className={cn(
                      "flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all",
                      formData.objetivo_principal === option.value
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                    )}
                  >
                    <RadioGroupItem value={option.value} id={option.value} className="sr-only" />
                    <div>
                      <span className="text-white font-medium block">{option.label}</span>
                      <span className="text-[#888] text-sm">{option.desc}</span>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-white mb-2 block">Peso objetivo (kg) - Opcional</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="70"
                value={formData.peso_objetivo}
                onChange={(e) => updateField("peso_objetivo", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">¿Qué te motiva? - Opcional</Label>
              <Textarea
                placeholder="Cuéntanos qué te ha llevado a dar este paso..."
                value={formData.motivacion}
                onChange={(e) => updateField("motivacion", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[100px]"
              />
            </div>
          </div>
        );

      case 2: // Training
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-white mb-3 block">Nivel de experiencia</Label>
              <RadioGroup
                value={formData.experiencia}
                onValueChange={(value) => updateField("experiencia", value)}
                className="space-y-3"
              >
                {[
                  { value: "principiante", label: "Principiante", desc: "0-6 meses entrenando" },
                  { value: "intermedio", label: "Intermedio", desc: "6 meses - 2 años" },
                  { value: "avanzado", label: "Avanzado", desc: "2+ años de experiencia" },
                ].map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`exp-${option.value}`}
                    className={cn(
                      "flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all",
                      formData.experiencia === option.value
                        ? "border-[#D4AF37] bg-[#D4AF37]/10"
                        : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                    )}
                  >
                    <RadioGroupItem value={option.value} id={`exp-${option.value}`} className="sr-only" />
                    <div>
                      <span className="text-white font-medium block">{option.label}</span>
                      <span className="text-[#888] text-sm">{option.desc}</span>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-white mb-3 block">Días disponibles para entrenar</Label>
              <div className="grid grid-cols-4 gap-2">
                {[3, 4, 5, 6].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => updateField("dias_disponibles", days.toString())}
                    className={cn(
                      "p-4 rounded-xl border-2 transition-all",
                      formData.dias_disponibles === days.toString()
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-[#2A2A2A] bg-[#1A1A1A] text-white hover:border-[#3A3A3A]"
                    )}
                  >
                    <span className="text-2xl font-bold">{days}</span>
                    <span className="block text-xs text-[#888]">días</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Lesiones o limitaciones - Opcional</Label>
              <Textarea
                placeholder="¿Tienes alguna lesión o limitación física?"
                value={formData.lesiones}
                onChange={(e) => updateField("lesiones", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Equipamiento disponible</Label>
              <Select value={formData.equipo_disponible} onValueChange={(v) => updateField("equipo_disponible", v)}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="gimnasio_completo" className="text-white">Gimnasio completo</SelectItem>
                  <SelectItem value="casa_basico" className="text-white">Casa - Básico (mancuernas)</SelectItem>
                  <SelectItem value="casa_completo" className="text-white">Casa - Completo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 3: // Nutrition
        return (
          <div className="space-y-6">
            <p className="text-[#888] text-sm mb-4">
              Esta información nos ayuda a personalizar tu dieta. Todos los campos son opcionales.
            </p>

            <div>
              <Label className="text-white mb-2 block">Alergias o intolerancias</Label>
              <Input
                placeholder="Lactosa, gluten, frutos secos..."
                value={formData.alergias}
                onChange={(e) => updateField("alergias", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Comidas que NO te gustan</Label>
              <Textarea
                placeholder="¿Hay alimentos que no puedes ni ver?"
                value={formData.comidas_no_gustan}
                onChange={(e) => updateField("comidas_no_gustan", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">Comidas favoritas</Label>
              <Textarea
                placeholder="Alimentos que te encantan y comes con frecuencia"
                value={formData.comidas_favoritas}
                onChange={(e) => updateField("comidas_favoritas", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>

            <div>
              <Label className="text-white mb-2 block">¿Cuántas comidas prefieres al día?</Label>
              <Select value={formData.horario_comidas} onValueChange={(v) => updateField("horario_comidas", v)}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="3_comidas" className="text-white">3 comidas</SelectItem>
                  <SelectItem value="4_comidas" className="text-white">4 comidas</SelectItem>
                  <SelectItem value="5_comidas" className="text-white">5 comidas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Suplementos que tomas actualmente</Label>
              <Input
                placeholder="Proteína, creatina, vitaminas..."
                value={formData.suplementos_actuales}
                onChange={(e) => updateField("suplementos_actuales", e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
          </div>
        );

      case 4: // Lifestyle
        return (
          <div className="space-y-6">
            <p className="text-[#888] text-sm mb-4">
              Tu estilo de vida influye en cómo diseñamos tu plan. Campos opcionales.
            </p>

            <div>
              <Label className="text-white mb-3 block">
                Nivel de estrés: <span className="text-[#D4AF37]">{formData.nivel_estres}/10</span>
              </Label>
              <Slider
                value={[formData.nivel_estres]}
                onValueChange={([value]) => updateField("nivel_estres", value)}
                max={10}
                min={1}
                step={1}
                className="py-4"
              />
              <div className="flex justify-between text-xs text-[#666]">
                <span>Muy bajo</span>
                <span>Muy alto</span>
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Horas de sueño por noche</Label>
              <Select value={formData.horas_sueno} onValueChange={(v) => updateField("horas_sueno", v)}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="5" className="text-white">Menos de 6h</SelectItem>
                  <SelectItem value="6" className="text-white">6-7 horas</SelectItem>
                  <SelectItem value="7" className="text-white">7-8 horas</SelectItem>
                  <SelectItem value="8" className="text-white">8+ horas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Nivel de actividad diaria</Label>
              <Select value={formData.nivel_actividad} onValueChange={(v) => updateField("nivel_actividad", v)}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="sedentario" className="text-white">Sedentario (sentado todo el día)</SelectItem>
                  <SelectItem value="ligero" className="text-white">Ligero (algo de movimiento)</SelectItem>
                  <SelectItem value="moderado" className="text-white">Moderado (bastante activo)</SelectItem>
                  <SelectItem value="activo" className="text-white">Activo (trabajo físico)</SelectItem>
                  <SelectItem value="muy_activo" className="text-white">Muy activo (deportista)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">Tipo de trabajo</Label>
              <Select value={formData.tipo_trabajo} onValueChange={(v) => updateField("tipo_trabajo", v)}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="oficina" className="text-white">Oficina / Escritorio</SelectItem>
                  <SelectItem value="fisico" className="text-white">Trabajo físico</SelectItem>
                  <SelectItem value="mixto" className="text-white">Mixto</SelectItem>
                  <SelectItem value="remoto" className="text-white">Remoto / Casa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const CurrentIcon = STEPS[currentStep].icon;

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="premium-onboarding">
      {/* Header */}
      <div className="sticky top-0 bg-[#0B0B0B]/95 backdrop-blur-lg border-b border-[#1A1A1A] px-4 py-4 z-10">
        <div className="flex items-center gap-4">
          {currentStep > 0 && (
            <button
              onClick={handleBack}
              className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[#888]" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <CurrentIcon className="w-5 h-5 text-[#D4AF37]" />
              {STEPS[currentStep].title}
            </h1>
            <p className="text-[#666] text-xs">Paso {currentStep + 1} de {STEPS.length}</p>
          </div>
          <div className="flex items-center gap-1">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold">GOAT</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1 mt-4">
          {STEPS.map((_, idx) => (
            <div
              key={idx}
              className={cn(
                "flex-1 h-1 rounded-full transition-colors",
                idx <= currentStep ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"
              )}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-6">
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B] to-transparent pt-8">
        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="w-full bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold py-6 disabled:opacity-50"
            data-testid="onboarding-next-btn"
          >
            Continuar
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold py-6"
            data-testid="onboarding-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Completar Perfil
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PremiumOnboarding;
